import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Button, Paper, Typography, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, List, ListItem, ListItemText, 
  Checkbox, Divider,
  Card, CardContent, CardActions, useMediaQuery, useTheme,
  Autocomplete, CircularProgress, FormControlLabel, Switch, Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { purchaseOrdersApi, tenantApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import SavedItemsSearch from './SavedItemsSearch';
import VendorSelector from './VendorSelector';
import { compressImages } from '../utils/imageCompression';
import { limitFilesForUpload } from '../utils/uploadLimits';

const ProjectPurchaseOrdersTab = ({ projectId, purchaseOrders, projectItems, refreshPurchaseOrders, project, currentUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const projectDepartment = project?.department || 'General';
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemModifications, setItemModifications] = useState({});
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorError, setVendorError] = useState("");
  const [referenceError, setReferenceError] = useState("");
  const [tenantBranding, setTenantBranding] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Duplication mode state
  const [isDuplicationMode, setIsDuplicationMode] = useState(false);
  const [selectedPOToDuplicate, setSelectedPOToDuplicate] = useState(null);
  const [allPurchaseOrders, setAllPurchaseOrders] = useState([]);
  const [loadingAllPOs, setLoadingAllPOs] = useState(false);
  
  // Saved items search state for adding new items
  const [savedItemsSearch, setSavedItemsSearch] = useState('');
  const [showAddItemSection, setShowAddItemSection] = useState(false);
  const [newItemData, setNewItemData] = useState({
    name: '',
    quantity: 1,
    price: 0,
    units: 'Each'
  });
  
  // Delivery address state
  const [deliveryAddress, setDeliveryAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    postcode: ''
  });
  const [reference, setReference] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const extractionFileInputRef = useRef(null);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [extractingItems, setExtractingItems] = useState(false);
  const [documentExtractionError, setDocumentExtractionError] = useState('');
  const [documentExtractionWarnings, setDocumentExtractionWarnings] = useState([]);
  const [documentExtractionNotes, setDocumentExtractionNotes] = useState('');

    // Fetch tenant branding on component mount
  useEffect(() => {
    const fetchTenantBranding = async () => {
      try {
        const response = await tenantApi.getBranding();
        setTenantBranding(response.data);
      } catch (error) {
        console.error('Error fetching tenant branding:', error);
      }
    };

    fetchTenantBranding();
  }, []);

  // Fetch all purchase orders when duplication mode is enabled
  useEffect(() => {
    const fetchAllPurchaseOrders = async () => {
      if (isDuplicationMode && allPurchaseOrders.length === 0) {
        try {
          setLoadingAllPOs(true);
          const response = await purchaseOrdersApi.getAll(true); // includeItems = true
          setAllPurchaseOrders(response.data);
        } catch (error) {
          console.error('Error fetching all purchase orders:', error);
        } finally {
          setLoadingAllPOs(false);
        }
      }
    };

    fetchAllPurchaseOrders();
  }, [isDuplicationMode, allPurchaseOrders.length]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  // Calculate NETT total (before VAT)
  const calculateNettTotal = (items) => {
    if (!items || !items.length) return 0;
    return items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  };

  // Mobile purchase order card component
  const PurchaseOrderCard = ({ po }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            PO-{po.purchase_order_no || (po.purchase_order_id ? po.purchase_order_id.toString().padStart(5, '0') : '0')}
          </Typography>
          <Chip 
            label={formatCurrency(
              po.items ? calculateNettTotal(po.items) : 0
            )}
            color="primary"
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Vendor:</strong> {po.vendor_name}
        </Typography>
        {po.reference && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Reference:</strong> {po.reference}
          </Typography>
        )}
        {po.due_date && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Due Date:</strong> {new Date(po.due_date).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Items:</strong> {po.items ? po.items.length : 0} items
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Created: {po.created_date ? new Date(po.created_date).toLocaleDateString('en-GB') : 'N/A'}
        </Typography>
      </CardContent>
      <CardActions sx={{ pt: 0, pb: 2, px: 2, gap: 1 }}>
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => handleViewDetails(po.purchase_order_id)}
        >
          View
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => handleOpenDeleteDialog(po)}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );

  // Helper to prefill delivery address
  const prefillAddress = (type) => {
    if (type === 'project' && project) {
      setDeliveryAddress({
        line1: project.address_line1 || '',
        line2: project.address_line2 || '',
        city: project.city || '',
        postcode: project.postcode || ''
      });
    } else if (type === 'company' && tenantBranding?.branding?.company_address) {
      // Parse company address string properly
      const companyAddress = tenantBranding.branding.company_address;
      const parts = companyAddress.split(',').map(s => s.trim());
      
      // UK postcode pattern - match at the end of the last part
      const lastPart = parts[parts.length - 1] || '';
      const postcodeMatch = lastPart.match(/([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})$/i);
      
      let postcode = '';
      let addressParts = [...parts];
      
      // If we found a postcode in the last part, extract it
      if (postcodeMatch) {
        postcode = postcodeMatch[1].toUpperCase();
        // Remove the postcode from the last part to get the city
        const cityFromLastPart = lastPart.substring(0, lastPart.length - postcode.length).trim();
        
        // Replace the last part with just the city (postcode removed)
        if (cityFromLastPart) {
          addressParts[parts.length - 1] = cityFromLastPart;
        } else {
          // If last part was only the postcode, remove it
          addressParts.pop();
        }
      }
      
      // Now split the remaining parts into line1, line2, and city
      const line1 = addressParts[0] || '';
      const line2 = addressParts[1] || '';
      const city = addressParts.length > 2 ? addressParts.slice(2).join(', ') : '';
      
      setDeliveryAddress({
        line1: line1,
        line2: line2,
        city: city,
        postcode: postcode
      });
    }
  };

  const handleOpenDialog = () => {
    setSelectedItems([]);
    setItemModifications({});
    setSelectedVendor(null);
    setVendorError("");
    setReferenceError("");
    setSavedItemsSearch('');
    setShowAddItemSection(false);
    setNewItemData({
      name: '',
      quantity: 1,
      price: 0,
      units: 'Each'
    });
    setReference(project?.quote_reference || '');
    setIsDuplicationMode(false);
    setSelectedPOToDuplicate(null);
    clearDocumentExtractionState();
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSavedItemsSearch('');
    setShowAddItemSection(false);
    setNewItemData({
      name: '',
      quantity: 1,
      price: 0,
      units: 'Each'
    });
    setReference('');
    setDueDate('');
    setContactName('');
    setContactNumber('');
    setReferenceError("");
    setIsDuplicationMode(false);
    setSelectedPOToDuplicate(null);
    clearDocumentExtractionState();
  };

  // Handle duplication mode toggle
  const handleDuplicationModeChange = (event) => {
    const enabled = event.target.checked;
    setIsDuplicationMode(enabled);
    
    if (!enabled) {
      // Reset to fresh PO mode
      setSelectedPOToDuplicate(null);
      setSelectedVendor(null);
      setDeliveryAddress({ line1: '', line2: '', city: '', postcode: '' });
      setReference(project?.quote_reference || '');
      setDueDate('');
      setContactName('');
      setContactNumber('');
      setSelectedItems([]);
      setItemModifications({});
      clearDocumentExtractionState();
    }
  };

  // Handle PO selection for duplication
  const handlePOSelection = async (po) => {
    if (!po) {
      setSelectedPOToDuplicate(null);
      setSelectedVendor(null);
      setDeliveryAddress({ line1: '', line2: '', city: '', postcode: '' });
      setReference(project?.quote_reference || '');
      setDueDate('');
      setContactName('');
      setContactNumber('');
      setSelectedItems([]);
      setItemModifications({});
      clearDocumentExtractionState();
      return;
    }

    setSelectedPOToDuplicate(po);
    
    // Pre-populate form with selected PO data
    // Set vendor (create vendor object from PO data)
    if (po.vendor_id) {
      setSelectedVendor({
        vendor_id: po.vendor_id,
        name: po.vendor_name
      });
    }
    
    // Set delivery address
    setDeliveryAddress({
      line1: po.delivery_address_line1 || '',
      line2: po.delivery_address_line2 || '',
      city: po.delivery_city || '',
      postcode: po.delivery_postcode || ''
    });
    
    // Set reference and other fields
    setReference(po.reference || '');
    setDueDate(po.due_date ? new Date(po.due_date).toISOString().split('T')[0] : '');
    setContactName(po.contact_name || '');
    setContactNumber(po.contact_number || '');
    
    // Set items from PO - create temporary IDs and add them as new items
    if (po.items && po.items.length > 0) {
      const tempItems = {};
      const tempSelectedItems = [];
      
      po.items.forEach((item, index) => {
        const tempId = `dup_${Date.now()}_${index}`;
        tempItems[tempId] = {
          name: item.name || '',
          quantity: item.quantity || 1,
          units: item.units || 'Each',
          price: item.price || 0
        };
        tempSelectedItems.push(tempId);
      });
      
      setItemModifications(tempItems);
      setSelectedItems(tempSelectedItems);
    }
  };
  
  const handleOpenDeleteDialog = (po) => {
    setSelectedPO(po);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleItemCheck = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };
  
  const handleItemModification = (itemId, field, value) => {
    setItemModifications(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [field]: value
      }
    }));
  };

  // Handle saved item selection for adding new item
  const handleSavedItemSelect = (savedItem) => {
    setNewItemData({
      name: savedItem.name,
      quantity: 1,
      price: 0,
      units: savedItem.units || 'Each'
    });
    setSavedItemsSearch('');
  };

  // Handle new item data changes
  const handleNewItemDataChange = (field, value) => {
    setNewItemData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isSupportedExtractionFile = (file) => {
    const fileType = (file?.type || '').toLowerCase();
    const fileName = (file?.name || '').toLowerCase();

    return (
      fileType.startsWith('image/') ||
      fileType === 'application/pdf' ||
      fileType === 'message/rfc822' ||
      fileName.endsWith('.pdf') ||
      fileName.endsWith('.eml') ||
      fileName.endsWith('.txt') ||
      fileName.endsWith('.html') ||
      fileName.endsWith('.htm')
    );
  };

  const clearDocumentExtractionState = () => {
    setDocumentFiles([]);
    setDocumentExtractionError('');
    setDocumentExtractionWarnings([]);
    setDocumentExtractionNotes('');
    if (extractionFileInputRef.current) {
      extractionFileInputRef.current.value = '';
    }
  };

  const handleDocumentFileSelect = async (event) => {
    const { files: selectedFiles, warning } = limitFilesForUpload(event.target.files || []);
    if (warning) {
      setDocumentExtractionError(warning);
      return;
    }

    const validFiles = selectedFiles.filter(isSupportedExtractionFile);

    if (validFiles.length !== selectedFiles.length) {
      setDocumentExtractionError('Some files were skipped. Only PDFs, images, and email files are supported.');
    } else {
      setDocumentExtractionError('');
    }

    if (!validFiles.length) {
      if (extractionFileInputRef.current) {
        extractionFileInputRef.current.value = '';
      }
      return;
    }

    try {
      const processedFiles = await compressImages(validFiles);
      setDocumentFiles((prev) => [...prev, ...processedFiles]);
      setDocumentExtractionWarnings([]);
      setDocumentExtractionNotes('');
    } catch (error) {
      console.error('Error preparing document files:', error);
      setDocumentExtractionError('Failed to prepare the selected file. Please try another document.');
    } finally {
      if (extractionFileInputRef.current) {
        extractionFileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveDocumentFile = (index) => {
    setDocumentFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleExtractItemsFromDocument = async () => {
    if (!documentFiles.length) {
      setDocumentExtractionError('Upload a PDF, image, or email file first.');
      return;
    }

    setExtractingItems(true);
    setDocumentExtractionError('');

    try {
      const formData = new FormData();
      documentFiles.forEach((file) => {
        formData.append('files', file);
      });

      const response = await purchaseOrdersApi.extractItems(formData);
      const extractedItems = Array.isArray(response.data?.items) ? response.data.items : [];

      if (!extractedItems.length) {
        setDocumentExtractionError('No items were found in the uploaded document.');
        return;
      }

      const tempItems = {};
      const tempSelectedItems = [];

      extractedItems.forEach((item, index) => {
        const tempId = `new_${Date.now()}_${index}`;
        tempItems[tempId] = {
          name: item.name || '',
          quantity: item.quantity || 1,
          units: item.units || 'Each',
          price: item.price || 0,
        };
        tempSelectedItems.push(tempId);
      });

      setItemModifications((prev) => ({ ...prev, ...tempItems }));
      setSelectedItems((prev) => [...prev, ...tempSelectedItems]);
      setDocumentExtractionWarnings(Array.isArray(response.data?.warnings) ? response.data.warnings : []);
      setDocumentExtractionNotes(response.data?.notes || '');
      setDocumentFiles([]);
    } catch (error) {
      console.error('Error extracting purchase order items:', error);
      setDocumentExtractionError(
        error.response?.data?.error || 'Failed to extract items from the uploaded document.'
      );
    } finally {
      setExtractingItems(false);
      if (extractionFileInputRef.current) {
        extractionFileInputRef.current.value = '';
      }
    }
  };

  // Add the new item to selected items (simulate it being a project item)
  const handleAddNewItem = () => {
    if (!newItemData.name.trim()) {
      return;
    }
    
    // Create a temporary ID for the new item (negative to distinguish from real project items)
    const tempId = `new_${Date.now()}`;
    
    // Add to itemModifications with all the new item data
    setItemModifications(prev => ({
      ...prev,
      [tempId]: {
        name: newItemData.name.trim(),
        quantity: newItemData.quantity,
        price: newItemData.price,
        units: newItemData.units || 'Each'
      }
    }));
    
    // Add to selected items
    setSelectedItems(prev => [...prev, tempId]);
    
    // Reset new item form
    setNewItemData({
      name: '',
      quantity: 1,
      price: 0,
      units: 'Each'
    });
    setSavedItemsSearch('');
    setShowAddItemSection(false);
  };
  
  const handleCreatePO = async () => {
    try {
      if (selectedItems.length === 0) {
        // Show error or notification
        return;
      }
      if (!selectedVendor) {
        setVendorError("Vendor is required");
        return;
      }
      if (!reference.trim()) {
        setReferenceError("Reference is required");
        return;
      }
      
      setCreating(true);
      
      // Separate project items from new/duplicated items
      const projectItemIds = selectedItems.filter(id => typeof id === 'number' || (!id.toString().startsWith('new_') && !id.toString().startsWith('dup_')));
      const newItems = selectedItems
        .filter(id => typeof id === 'string' && (id.startsWith('new_') || id.startsWith('dup_')))
        .map(id => itemModifications[id]);
      
      // Prepare modifications for project items only
      const projectItemModifications = {};
      Object.keys(itemModifications).forEach(id => {
        if (!id.toString().startsWith('new_') && !id.toString().startsWith('dup_')) {
          projectItemModifications[id] = itemModifications[id];
        }
      });
      
      await purchaseOrdersApi.create(projectId, {
        selected_item_ids: projectItemIds,
        item_modifications: projectItemModifications,
        new_items: newItems, // Add new items and duplicated items separately
        vendor_id: selectedVendor.vendor_id,
        department: projectDepartment,
        delivery_address_line1: deliveryAddress.line1,
        delivery_address_line2: deliveryAddress.line2,
        delivery_city: deliveryAddress.city,
        delivery_postcode: deliveryAddress.postcode,
        reference: reference.trim() || null,
        due_date: dueDate || null,
        contact_name: contactName.trim() || null,
        contact_number: contactNumber.trim() || null
      });
      handleCloseDialog();
      refreshPurchaseOrders();
    } catch (error) {
      console.error('Error creating purchase order:', error);
    } finally {
      setCreating(false);
    }
  };
  
  const handleDelete = async () => {
    try {
      await purchaseOrdersApi.delete(selectedPO.purchase_order_id);
      handleCloseDeleteDialog();
      refreshPurchaseOrders();
    } catch (error) {
      console.error('Error deleting purchase order:', error);
    }
  };
  
  const handleViewDetails = (poId) => {
    navigate(`/purchase-orders/${poId}`);
  };
  
  // Desktop columns
  const columns = [
    {
      field: 'number',
      headerName: 'PO Number',
      width: 120,
      renderCell: (params) => `PO-${params.row.purchase_order_no || (params.row.purchase_order_id ? params.row.purchase_order_id.toString().padStart(5, '0') : '0')}`,
    },
    { 
      field: 'created_date', 
      headerName: 'Created Date', 
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-GB');
      }
    },
    {
      field: 'vendor_name',
      headerName: 'Vendor',
      width: 180,
    },
    {
      field: 'reference',
      headerName: 'Reference',
      width: 150,
      valueFormatter: (params) => params.value || '-'
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      }
    },
    {
      field: 'total_value',
      headerName: 'NETT Total',
      width: 150,
      valueGetter: (params) => {
        // Calculate total from items
        if (!params.row.items) return 0;
        return calculateNettTotal(params.row.items);
      },
      valueFormatter: (params) => formatCurrency(params.value)
    },
    {
      field: 'item_count',
      headerName: 'Items',
      width: 100,
      valueGetter: (params) => params.row.items ? params.row.items.length : 0
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleViewDetails(params.row.purchase_order_id)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenDeleteDialog(params.row)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', width: '100%', p: { xs: 1, md: 2 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'flex-end', 
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 2,
        gap: 2
      }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          fullWidth={isMobile}
        >
          Create Purchase Order
        </Button>
      </Box>
      
      {purchaseOrders.length > 0 ? (
        <>
          {/* Mobile view - Cards */}
          {isMobile ? (
            <Box>
              {purchaseOrders.map((po) => (
                <PurchaseOrderCard key={po.purchase_order_id} po={po} />
              ))}
            </Box>
          ) : (
            /* Desktop view - DataGrid */
            <Paper sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={purchaseOrders}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10]}
                getRowId={(row) => row.purchase_order_id}
                disableSelectionOnClick
              />
            </Paper>
          )}
        </>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">No purchase orders created yet.</Typography>
        </Paper>
      )}
      
      {/* Create Purchase Order Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Create Purchase Order</DialogTitle>
        <DialogContent>
          {/* Duplication Mode Toggle */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isDuplicationMode}
                  onChange={handleDuplicationModeChange}
                  color="primary"
                />
              }
              label="Duplicate from existing purchase order"
            />
          </Box>

          {/* PO Selector for Duplication */}
          {isDuplicationMode && (
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                options={allPurchaseOrders} // All purchase orders from the system
                getOptionLabel={(option) => {
                  const poNumber = `PO-${option.purchase_order_no || option.purchase_order_id}`;
                  const vendor = option.vendor_name;
                  const reference = option.reference || 'No reference';
                  const origin = option.project_id ? 'Project' : 'Standalone';
                  return `${poNumber} - ${vendor} - ${reference} (${origin})`;
                }}
                value={selectedPOToDuplicate}
                onChange={(event, newValue) => handlePOSelection(newValue)}
                loading={loadingAllPOs}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Purchase Order to Duplicate"
                    required
                    helperText="Choose any purchase order (from any project or standalone) to duplicate"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAllPOs ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        PO-{option.purchase_order_no || option.purchase_order_id} - {option.vendor_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.reference || 'No reference'} • {option.items ? option.items.length : 0} items • {formatCurrency(calculateNettTotal(option.items || []))}
                        {option.project_id && ` • Project PO`}
                        {!option.project_id && ` • Standalone PO`}
                      </Typography>
                    </Box>
                    <Chip 
                      label={option.status || 'New'} 
                      size="small" 
                      color={option.status === 'Completed' ? 'success' : 'default'}
                      sx={{ ml: 1 }}
                    />
                  </Box>
                )}
              />
            </Box>
          )}

          {selectedPOToDuplicate && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Duplicating from:</strong> PO-{selectedPOToDuplicate.purchase_order_no || selectedPOToDuplicate.purchase_order_id} - {selectedPOToDuplicate.vendor_name}
              </Typography>
              <Typography variant="body2">
                All fields are pre-filled but can be modified before creating.
              </Typography>
            </Alert>
          )}

          <VendorSelector
            selectedVendor={selectedVendor}
            onVendorChange={(vendor) => {
              setSelectedVendor(vendor);
              setVendorError("");
            }}
            disabled={isDuplicationMode && !selectedPOToDuplicate}
          />
          {vendorError && (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
              {vendorError}
            </Typography>
          )}
          {isDuplicationMode && selectedPOToDuplicate && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Vendor is pre-filled from the selected purchase order, but you can change it if needed.
            </Typography>
          )}

          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Department"
              value={projectDepartment}
              disabled
              size="small"
              helperText="Inherited from the project"
            />
          </Box>

          {/* Reference Field */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Reference *"
              value={reference}
              onChange={(e) => {
                setReference(e.target.value);
                setReferenceError("");
              }}
              placeholder="Enter purchase order reference"
              size="small"
              error={!!referenceError}
              helperText={referenceError}
            />
          </Box>

          {/* Due Date Field */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              placeholder=""
              inputProps={{
                placeholder: ""
              }}
            />
          </Box>

          {/* Contact Details */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Contact Details (Optional)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  size="small"
                  placeholder="Enter contact person name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  size="small"
                  placeholder="Enter contact phone number"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Display duplicated items */}
          {isDuplicationMode && selectedItems.filter(id => typeof id === 'string' && id.startsWith('dup_')).length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Duplicated Items (from selected PO)
              </Typography>
              <List dense>
                {selectedItems
                  .filter(id => typeof id === 'string' && id.startsWith('dup_'))
                  .map((itemId) => {
                    const dupItemData = itemModifications[itemId];
                    return (
                      <React.Fragment key={itemId}>
                        <ListItem alignItems="flex-start">
                          <Checkbox
                            edge="start"
                            checked={true}
                            onChange={() => {
                              // Remove from selected items
                              setSelectedItems(prev => prev.filter(id => id !== itemId));
                              // Remove from modifications
                              setItemModifications(prev => {
                                const newMods = { ...prev };
                                delete newMods[itemId];
                                return newMods;
                              });
                            }}
                          />
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  label="Item Name"
                                  value={dupItemData?.name || ''}
                                  onChange={e => handleItemModification(itemId, 'name', e.target.value)}
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  maxRows={10}
                                />
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <TextField
                                  label="Quantity"
                                  type="number"
                                  value={dupItemData?.quantity || 1}
                                  onChange={e => handleItemModification(itemId, 'quantity', Number(e.target.value))}
                                  size="small"
                                  fullWidth
                                  inputProps={{ min: 1 }}
                                />
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <TextField
                                  label="Units"
                                  value={dupItemData?.units || 'Each'}
                                  onChange={e => handleItemModification(itemId, 'units', e.target.value)}
                                  size="small"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={4} sm={3}>
                                <TextField
                                  label="Price"
                                  type="number"
                                  value={dupItemData?.price || 0}
                                  onChange={e => handleItemModification(itemId, 'price', Number(e.target.value))}
                                  size="small"
                                  fullWidth
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    );
                  })}
              </List>
            </>
          )}

          {/* Project Items Selection - Hide for Site Manager users */}
          {currentUser?.role !== 'Site Manager' && (!isDuplicationMode || (isDuplicationMode && selectedPOToDuplicate)) && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                {isDuplicationMode ? 'Add Items from Project (Optional)' : 'Select Items for Purchase Order'}
              </Typography>
              
              <List dense>
                {projectItems.map((item) => {
                  const isSelected = selectedItems.includes(item.item_id);
                  const modified = itemModifications[item.item_id] || {};
                  return (
                    <React.Fragment key={item.item_id}>
                      <ListItem alignItems="flex-start">
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          onChange={() => handleItemCheck(item.item_id)}
                        />
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {isSelected ? (
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  label="Item Name"
                                  value={modified.name !== undefined ? modified.name : item.name}
                                  onChange={e => handleItemModification(item.item_id, 'name', e.target.value)}
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  maxRows={10}
                                />
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <TextField
                                  label="Quantity"
                                  type="number"
                                  value={modified.quantity !== undefined ? modified.quantity : item.quantity}
                                  onChange={e => handleItemModification(item.item_id, 'quantity', Number(e.target.value))}
                                  size="small"
                                  fullWidth
                                  inputProps={{ min: 1 }}
                                />
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <TextField
                                  label="Units"
                                  value={modified.units !== undefined ? modified.units : (item.units || 'Each')}
                                  onChange={e => handleItemModification(item.item_id, 'units', e.target.value)}
                                  size="small"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={4} sm={3}>
                                <TextField
                                  label="Price"
                                  type="number"
                                  value={modified.price !== undefined ? modified.price : item.list_price}
                                  onChange={e => handleItemModification(item.item_id, 'price', Number(e.target.value))}
                                  size="small"
                                  fullWidth
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              </Grid>
                            </Grid>
                          ) : (
                            <ListItemText
                              primary={item.name}
                              secondary={`Quantity: ${item.quantity} ${item.units ? `(${item.units})` : ''} • Price: ${formatCurrency(item.list_price)}`}
                            />
                          )}
                        </Box>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  );
                })}
              </List>
            </>
          )}
          
          {/* Display newly added items */}
          {selectedItems.filter(id => typeof id === 'string' && id.startsWith('new_')).length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Newly Added Items
              </Typography>
              <List dense>
                {selectedItems
                  .filter(id => typeof id === 'string' && id.startsWith('new_'))
                  .map((itemId) => {
                    const newItemData = itemModifications[itemId];
                    return (
                      <React.Fragment key={itemId}>
                        <ListItem alignItems="flex-start">
                          <Checkbox
                            edge="start"
                            checked={true}
                            onChange={() => setSelectedItems(prev => prev.filter(id => id !== itemId))}
                          />
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  label="Item Name"
                                  value={newItemData?.name || ''}
                                  onChange={e => handleItemModification(itemId, 'name', e.target.value)}
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  maxRows={10}
                                />
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <TextField
                                  label="Quantity"
                                  type="number"
                                  value={newItemData?.quantity || 1}
                                  onChange={e => handleItemModification(itemId, 'quantity', Number(e.target.value))}
                                  size="small"
                                  fullWidth
                                  inputProps={{ min: 1 }}
                                />
                              </Grid>
                              <Grid item xs={4} sm={2}>
                                <TextField
                                  label="Units"
                                  value={newItemData?.units || 'Each'}
                                  onChange={e => handleItemModification(itemId, 'units', e.target.value)}
                                  size="small"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={4} sm={3}>
                                <TextField
                                  label="Price"
                                  type="number"
                                  value={newItemData?.price || 0}
                                  onChange={e => handleItemModification(itemId, 'price', Number(e.target.value))}
                                  size="small"
                                  fullWidth
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    );
                  })}
              </List>
            </>
          )}
          
          {/* Add Items Manually */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {currentUser?.role === 'Site Manager' ? 'Add Items' : 'Add Additional Items'}
            </Typography>
            <input
              ref={extractionFileInputRef}
              type="file"
              hidden
              multiple
              accept="image/*,.pdf,.eml,.txt,.html,.htm"
              onChange={handleDocumentFileSelect}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => extractionFileInputRef.current?.click()}
                disabled={creating || extractingItems}
              >
                Add From Document
              </Button>
              <Button
                variant="outlined"
                startIcon={extractingItems ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                onClick={handleExtractItemsFromDocument}
                disabled={creating || extractingItems || documentFiles.length === 0}
              >
                {extractingItems ? 'Extracting...' : 'Extract Items'}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Upload a supplier PDF, image, or email and extract line items directly into this project purchase order.
            </Typography>
            {documentFiles.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {documentFiles.map((file, index) => (
                  <Chip
                    key={`${file.name}-${index}`}
                    label={file.name}
                    onDelete={() => handleRemoveDocumentFile(index)}
                    size="small"
                  />
                ))}
              </Box>
            )}
            {documentExtractionError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {documentExtractionError}
              </Alert>
            )}
            {documentExtractionWarnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {documentExtractionWarnings.join(' ')}
              </Alert>
            )}
            {documentExtractionNotes && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {documentExtractionNotes}
              </Alert>
            )}
            {!showAddItemSection ? (
              <Button 
                variant="outlined" 
                onClick={() => setShowAddItemSection(true)}
                startIcon={<AddIcon />}
              >
                {currentUser?.role === 'Site Manager' ? 'Add Item' : 'Add Item from Saved Items'}
              </Button>
            ) : (
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                {currentUser?.role !== 'Site Manager' && (
                  <SavedItemsSearch
                    value={savedItemsSearch}
                    onChange={setSavedItemsSearch}
                    onSelect={handleSavedItemSelect}
                    placeholder="Search saved items or type new item name..."
                    size="small"
                  />
                )}
                
                <Grid container spacing={2} sx={{ mt: currentUser?.role === 'Site Manager' ? 0 : 1 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Item Name *"
                      value={newItemData.name}
                      onChange={(e) => handleNewItemDataChange('name', e.target.value)}
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={10}
                    />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      label="Quantity *"
                      type="number"
                      value={newItemData.quantity}
                      onChange={(e) => handleNewItemDataChange('quantity', Number(e.target.value))}
                      size="small"
                      fullWidth
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      label="Units"
                      value={newItemData.units}
                      onChange={(e) => handleNewItemDataChange('units', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <TextField
                      label="Price (£) *"
                      type="number"
                      value={newItemData.price}
                      onChange={(e) => handleNewItemDataChange('price', Number(e.target.value))}
                      size="small"
                      fullWidth
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        onClick={handleAddNewItem}
                        disabled={!newItemData.name.trim()}
                        size="small"
                      >
                        Add
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setShowAddItemSection(false)}
                        size="small"
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Box>
          
          {selectedItems.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                PO Summary
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2">Items Selected:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight="bold">
                    {selectedItems.length}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">NETT Total:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(
                      selectedItems.reduce((total, itemId) => {
                        if (typeof itemId === 'string' && (itemId.startsWith('new_') || itemId.startsWith('dup_'))) {
                          // This is a new or duplicated item, get data from itemModifications
                          const itemMod = itemModifications[itemId];
                          const price = itemMod?.price || 0;
                          const quantity = itemMod?.quantity || 0;
                          return total + (price * quantity);
                        } else {
                          // This is a project item
                          const item = projectItems.find(i => i.item_id === itemId);
                          const modified = itemModifications[itemId] || {};
                          const price = modified.price !== undefined ? modified.price : (item ? item.list_price : 0);
                          const quantity = modified.quantity !== undefined ? modified.quantity : (item ? item.quantity : 0);
                          return total + (price * quantity);
                        }
                      }, 0)
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Delivery Address</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={() => prefillAddress('project')}>
                Use Project Address
              </Button>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => {
                  console.log('Company address button clicked', tenantBranding);
                  prefillAddress('company');
                }}
              >
                Use Company Address
              </Button>
            </Box>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  label="Address Line 1" 
                  value={deliveryAddress.line1} 
                  onChange={e => setDeliveryAddress(a => ({ ...a, line1: e.target.value }))} 
                  fullWidth 
                  size="small" 
                  required 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  label="Address Line 2" 
                  value={deliveryAddress.line2} 
                  onChange={e => setDeliveryAddress(a => ({ ...a, line2: e.target.value }))} 
                  fullWidth 
                  size="small" 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  label="City" 
                  value={deliveryAddress.city} 
                  onChange={e => setDeliveryAddress(a => ({ ...a, city: e.target.value }))} 
                  fullWidth 
                  size="small" 
                  required 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  label="Postcode" 
                  value={deliveryAddress.postcode} 
                  onChange={e => setDeliveryAddress(a => ({ ...a, postcode: e.target.value }))} 
                  fullWidth 
                  size="small" 
                  required 
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
          <Button onClick={handleCloseDialog} fullWidth={isMobile} disabled={creating}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreatePO} 
            variant="contained" 
            color="primary"
            disabled={selectedItems.length === 0 || creating}
            fullWidth={isMobile}
          >
            {creating ? <CircularProgress size={20} /> : (isDuplicationMode ? 'Duplicate Purchase Order' : 'Create Purchase Order')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCloseDeleteDialog}
        fullScreen={isMobile}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this purchase order?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
          <Button onClick={handleCloseDeleteDialog} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            fullWidth={isMobile}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectPurchaseOrdersTab;